import * as React from "react";
import styled, { css } from "styled-components";
import { Spinner, Icon, Pluralize } from "components/common";
import { Button } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useThreatRelatedEventDefinitions from "./useThreatRelatedEventDefinitions";
export const STEPS = {
  overview: {
    key: "overview",
    title: (coverageEntity, isTactic) => coverageEntity ? /* @__PURE__ */ React.createElement(React.Fragment, null, "Improve Threat Coverage For ", /* @__PURE__ */ React.createElement("i", null, coverageEntity.name), " ", isTactic ? "Tactic" : "Technique") : "Improve Threat Coverage For All Tactics/Techniques"
  },
  illuminatePacksEnable: {
    key: "illuminatePacksEnable",
    title: () => "Enable Illuminate Packs",
    index: 0
  },
  eventDefinitionsEnable: {
    key: "eventDefinitionsEnable",
    title: () => "Enable Event Definitions",
    index: 1
  }
};
const TimelineContainer = styled.div`
  margin-top: 20px;
`;
const StepTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const TimelineItem = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 20px;
`;
const StepIconContainer = styled.div`
  width: 50px;
`;
const StepIcon = ({ isComplete }) => /* @__PURE__ */ React.createElement(StepIconContainer, { $isComplete: isComplete }, /* @__PURE__ */ React.createElement(
  Icon,
  {
    name: isComplete ? "check_circle" : "circle",
    bsStyle: isComplete ? "success" : "warning",
    type: "regular",
    size: "2x"
  }
));
const LeftCol = styled.div`
  flex: 1;
`;
const StepTitle = ({
  title,
  enableConfBtn,
  onConfigure,
  highlightConfBtn,
  step,
  disabled = false
}) => {
  const sendTelemetry = useSendTelemetry("threat-coverage");
  const _onConfigure = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.THREAT_COVERAGE.IMPROVE_COVERAGE_STEP_OPENED, {
      app_pathname: "security",
      event_details: { step }
    });
    onConfigure();
  };
  return /* @__PURE__ */ React.createElement(StepTitleContainer, null, title, enableConfBtn && /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled,
      bsSize: "xsmall",
      bsStyle: highlightConfBtn ? "info" : "default",
      onClick: _onConfigure
    },
    "configure"
  ));
};
const useThreatRelatedEventDefinitionsAreEnabled = (coverageEntity) => {
  const { loadingDefinitions, notEnabledDefinitions, allDefinitions } = useThreatRelatedEventDefinitions(coverageEntity);
  return {
    amountNotEnabledDefinitions: notEnabledDefinitions.length,
    amountAllDefinitions: allDefinitions.length,
    definitionsAreEnabled: allDefinitions.length > 0 && notEnabledDefinitions.length === 0,
    isLoadingDefinitions: loadingDefinitions
  };
};
const Description = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
  `
);
const _activeStepIndex = (illuminatePacksAreEnabled) => {
  if (!illuminatePacksAreEnabled) {
    return STEPS.illuminatePacksEnable.index;
  }
  return STEPS.eventDefinitionsEnable.index;
};
const StepsOverview = ({
  setStep,
  coverageEntity,
  isTactic,
  illuminatePacksAreEnabled
}) => {
  const { isIlluminatePacksAreEnabled, isLoadingPacks, amountNotEnabledPacks, amountAllPacks } = illuminatePacksAreEnabled;
  const { definitionsAreEnabled, isLoadingDefinitions, amountAllDefinitions, amountNotEnabledDefinitions } = useThreatRelatedEventDefinitionsAreEnabled(coverageEntity);
  const isEventDefinitionsStepAvailable = !isLoadingPacks && amountNotEnabledPacks !== amountAllPacks;
  if (isLoadingPacks || isLoadingDefinitions) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const activeStepIndex = _activeStepIndex(isIlluminatePacksAreEnabled);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", null, "Follow these steps to increase your threat coverage."), /* @__PURE__ */ React.createElement(TimelineContainer, null, /* @__PURE__ */ React.createElement(TimelineItem, null, /* @__PURE__ */ React.createElement(StepIcon, { isComplete: isIlluminatePacksAreEnabled }), /* @__PURE__ */ React.createElement(LeftCol, null, /* @__PURE__ */ React.createElement(
    StepTitle,
    {
      title: STEPS.illuminatePacksEnable.title(coverageEntity, isTactic),
      step: STEPS.illuminatePacksEnable.key,
      onConfigure: () => setStep(STEPS.illuminatePacksEnable.key),
      enableConfBtn: !isIlluminatePacksAreEnabled,
      highlightConfBtn: activeStepIndex === STEPS.illuminatePacksEnable.index
    }
  ), /* @__PURE__ */ React.createElement(Description, null, amountAllPacks === 0 ? "Please complete the previous steps first." : /* @__PURE__ */ React.createElement(React.Fragment, null, isIlluminatePacksAreEnabled ? "All related Illuminate Packs, have been enabled." : /* @__PURE__ */ React.createElement(React.Fragment, null, "There ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "is", plural: "are", value: amountNotEnabledPacks }), " ", /* @__PURE__ */ React.createElement("b", null, amountNotEnabledPacks), " threat coverage related Illuminate", " ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "Pack", plural: "Packs", value: amountNotEnabledPacks }), " which", " ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "needs", plural: "need", value: amountNotEnabledPacks }), " to be enabled."))))), /* @__PURE__ */ React.createElement(TimelineItem, null, /* @__PURE__ */ React.createElement(StepIcon, { isComplete: definitionsAreEnabled }), /* @__PURE__ */ React.createElement(LeftCol, null, /* @__PURE__ */ React.createElement(
    StepTitle,
    {
      title: STEPS.eventDefinitionsEnable.title(coverageEntity, isTactic),
      step: STEPS.eventDefinitionsEnable.key,
      onConfigure: () => setStep(STEPS.eventDefinitionsEnable.key),
      enableConfBtn: true,
      highlightConfBtn: activeStepIndex === STEPS.eventDefinitionsEnable.index,
      disabled: !isEventDefinitionsStepAvailable
    }
  ), /* @__PURE__ */ React.createElement(Description, null, amountAllDefinitions === 0 ? "No event definitions reference these tactics/techniques yet." : /* @__PURE__ */ React.createElement(React.Fragment, null, definitionsAreEnabled ? "All related event definitions have been enabled." : /* @__PURE__ */ React.createElement(React.Fragment, null, "There ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "is", plural: "are", value: amountNotEnabledDefinitions }), " ", /* @__PURE__ */ React.createElement("b", null, amountNotEnabledDefinitions), " related Event", " ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "Definition", plural: "Definitions", value: amountNotEnabledDefinitions }), " which", " ", /* @__PURE__ */ React.createElement(Pluralize, { singular: "needs", plural: "need", value: amountNotEnabledDefinitions }), " to be enabled.")))))));
};
export default StepsOverview;
